import AdminRegistryReset from "../components/AdminRegistryReset";
import AdminDataRequests from "../components/AdminDataRequests";
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckCircle, XCircle, ShieldCheck, MapPin, SignOut, Warning,
    ClipboardText,
} from "@phosphor-icons/react";
import { endpoints, adminToken, api } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
const SECTIONS = [{ key: "datasets", label: "Contributions", singular: "contribution" }];

// map section key → payload kind (server expects singular kind names)
const KIND_MAP = {
    countries: "country",
    categories: "category",
    materials: "material",
    material_types: "material_type",
    processes: "process",
    parameters: "parameter",
    datasets: "dataset",
};

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState("datasets");
    const [busyId, setBusyId] = useState(null);
    const [busyAll, setBusyAll] = useState(false);
    const navigate = useNavigate();
    const [exportStatus, setExportStatus] = useState("all");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [exporting, setExporting] = useState(false);
    const downloadData = async () => {
        if (fromDate && toDate && fromDate > toDate) { toast.error("Start date must be on or before end date"); return; }
        setExporting(true);
        try {
            const response = await api.get("/admin/export", { responseType: "blob", headers: {Authorization: `Bearer ${adminToken.get()}`}, params: {approval: exportStatus, uploaded_from: fromDate || undefined, uploaded_to: toDate || undefined} });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a"); link.href = url; link.download = "SustainDex_Data_Export.xlsx"; document.body.appendChild(link); link.click(); link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("Excel download ready");
        } catch (error) {
            let message = "Download failed. Please try again.";
            try { message = JSON.parse(await error.response.data.text()).detail || message; } catch (_) {}
            toast.error(message);
        } finally { setExporting(false); }
    };

    const load = useCallback(async () => {
        const t = adminToken.get();
        if (!t) { navigate("/admin/login"); return; }
        try {
            const r = await endpoints.adminPending(t);
            setData(r);
        } catch (e) {
            if (e?.response?.status === 401) {
                adminToken.clear();
                navigate("/admin/login");
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const logout = () => {
        adminToken.clear();
        navigate("/admin/login");
    };

    const review = async (kind, id, action) => {
        setBusyId(id);
        try {
            const t = adminToken.get();
            await endpoints.adminReview(t, { kind, item_id: id, action });
            toast.success(action === "approve" ? "Contribution and its directory path approved" : "Contribution rejected");
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Action failed");
        } finally {
            setBusyId(null);
        }
    };

    const approveAll = async () => {
        const section = SECTIONS.find((item) => item.key === active);
        const count = data?.counts?.[active] || 0;
        if (!section || count === 0) return;
        const confirmed = window.confirm(
            `Approve all ${count} pending ${section.label.toLowerCase()}? This will make them public immediately.`
        );
        if (!confirmed) return;
        setBusyAll(true);
        try {
            const response = await endpoints.adminReviewAll(
                adminToken.get(), KIND_MAP[active]
            );
            toast.success(`Approved ${response.approved} ${section.label.toLowerCase()}`);
            await load();
        } catch (_) {
            toast.error("Approve all failed");
        } finally {
            setBusyAll(false);
        }
    };

    if (loading) {
        return <div className="p-10 text-sm text-neutral-500">Loading admin queue…</div>;
    }

    const counts = data?.counts || {};
    const items = data?.[active] || [];

    return (
        <div className="max-w-[1400px] mx-auto px-6 py-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="label-caps mb-2 flex items-center gap-2">
                        <ShieldCheck size={12} weight="fill" />
                        Administrator
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight">
                        Review queue
                    </h1>
                    <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
                        Review each contribution once. Approving it publishes the value and any new item, process and parameter in its path together.
                    </p>
                </div>
                <button
                    data-testid={TEST_IDS.adminLogoutButton}
                    onClick={logout}
                    className="flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded-sm text-sm hover:bg-neutral-50"
                >
                    <SignOut size={14} /> Sign out
                </button>
            </div>

            <section className="border border-neutral-200 rounded-lg p-5 mb-6 bg-white">
                <h2 className="text-lg font-semibold">Download data as Excel</h2>
                <p className="text-sm text-neutral-500 mt-1 mb-4">Download all submitted data, or filter by upload date and current approval status. Includes full source details and hierarchy. Dates are inclusive (UTC); leave blank for all dates.</p>
                <div className="flex flex-wrap items-end gap-4">
                    <label className="text-sm flex flex-col gap-1">Approval status<select className="border rounded px-3 py-2" value={exportStatus} onChange={e => setExportStatus(e.target.value)}><option value="all">All data — all statuses</option><option value="pending">Before approval — pending</option><option value="approved">After approval — approved</option><option value="rejected">Rejected</option></select></label>
                    <label className="text-sm flex flex-col gap-1">Uploaded from<input type="date" className="border rounded px-3 py-2" value={fromDate} onChange={e => setFromDate(e.target.value)} /></label>
                    <label className="text-sm flex flex-col gap-1">Uploaded through<input type="date" className="border rounded px-3 py-2" value={toDate} onChange={e => setToDate(e.target.value)} /></label>
                    <button onClick={downloadData} disabled={exporting} className="btn-primary px-4 py-2 disabled:opacity-50">{exporting ? "Preparing Excel…" : "Download Excel"}</button>
                </div>
            </section>
            {/* Section tabs */}
            <AdminRegistryReset onReset={load}/>
            <AdminDataRequests />
            <div className="flex items-center gap-2 overflow-x-auto border-b border-neutral-200 mb-6">
                {SECTIONS.map((s) => {
                    const c = counts[s.key] || 0;
                    const isActive = active === s.key;
                    return (
                        <button
                            key={s.key}
                            data-testid={TEST_IDS.adminPendingSection}
                            onClick={() => setActive(s.key)}
                            className={[
                                "px-4 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors",
                                isActive
                                    ? "border-[#6B46C1] text-[#4C1D95] font-semibold"
                                    : "border-transparent text-neutral-600 hover:text-black",
                            ].join(" ")}
                        >
                            {s.label}
                            <span className={[
                                "text-[10px] font-mono px-1.5 py-0.5 rounded-sm",
                                c > 0
                                    ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                    : "bg-neutral-100 text-neutral-500 border border-neutral-200",
                            ].join(" ")}>
                                {c}
                            </span>
                        </button>
                    );
                })}
                <div className="ml-auto label-caps text-neutral-400 pr-2">
                    Pending contributions {counts.datasets ?? 0}
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 mb-4">
                <p className="text-xs text-neutral-500">
                    Reviewing {SECTIONS.find((section) => section.key === active)?.label.toLowerCase()}.
                </p>
                <button
                    type="button"
                    data-testid={TEST_IDS.adminApproveAllButton}
                    onClick={approveAll}
                    disabled={!items.length || busyAll || !!busyId}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#16A34A] text-white text-sm font-semibold rounded-sm hover:bg-[#15803D] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <CheckCircle size={16} weight="bold" />
                    {busyAll ? "Approving all…" : `Approve all (${counts[active] || 0})`}
                </button>
            </div>

            {/* Cards */}
            {items.length === 0 ? (
                <div className="border border-dashed border-neutral-300 bg-neutral-50 rounded-sm p-10 text-center">
                    <CheckCircle size={32} weight="duotone" className="mx-auto text-[#16A34A] mb-3" />
                    <div className="font-serif text-2xl">Queue empty</div>
                    <p className="text-xs text-neutral-500 mt-2 max-w-md mx-auto">
                        There are no pending {SECTIONS.find((s) => s.key === active).label.toLowerCase()}.
                        You&apos;ll see submissions here as contributors send them.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {items.map((it) => (
                        <PendingCard
                            key={it.id}
                            item={it}
                            kind={KIND_MAP[active]}
                            busy={busyId === it.id}
                            onApprove={() => review(KIND_MAP[active], it.id, "approve")}
                            onReject={() => review(KIND_MAP[active], it.id, "reject")}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}


function PendingCard({ item, kind, busy, onApprove, onReject }) {
    const ctx = item._ctx || {};
    const crumbs = [
        ctx.country?.name,
        ctx.category?.name,
        ctx.material?.name,
        ctx.material_type?.name,
        ctx.process?.name,
        ctx.parameter?.name,
    ].filter(Boolean);

    return (
        <div
            data-testid={TEST_IDS.adminPendingCard}
            className="border border-neutral-200 bg-white rounded-sm overflow-hidden"
        >
            <div className="px-4 py-3 border-b border-neutral-100 bg-[#FCFCFB] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="label-caps">{kind.replace("_", " ")}</span>
                    <span className="text-[10px] px-1.5 py-0.5 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded-sm uppercase tracking-wider font-semibold">
                        Pending
                    </span>
                </div>
                <div className="text-[11px] font-mono text-neutral-400">
                    {new Date(item.created_at || Date.now()).toLocaleDateString()}
                </div>
            </div>

            <div className="p-4">
                <div className="font-serif text-2xl leading-tight">
                    {kind === "dataset"
                        ? `${item.organization_name} — ${item.value} ${item.unit}`
                        : item.name}
                </div>

                {crumbs.length > 0 && (
                    <div className="mt-3 flex items-start gap-2 text-xs text-neutral-500 flex-wrap">
                        <MapPin size={12} className="mt-0.5 shrink-0" />
                        <span className="flex-1">
                            {crumbs.join(" › ")}
                        </span>
                    </div>
                )}

                {kind === "dataset" && (
                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                        <Kpi k="Value" v={`${item.value} ${item.unit}`} />
                        <Kpi k="Methodology" v={item.methodology} />
                        <Kpi k="Geography" v={item.geography} />
                        <Kpi k="Year" v={item.year} />
                        <Kpi k="Confidence" v={`${item.confidence}%`} />
                        <Kpi k="Peer reviewed" v={item.peer_reviewed ? "Yes" : "No"} />
                    </div>
                )}

                {item.source_url && (
                    <div className="mt-3 text-xs break-all">
                        <span className="label-caps mr-2">Source</span>
                        <a
                            className="text-[#6B46C1] underline underline-offset-2"
                            href={item.source_url}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {item.source_url}
                        </a>
                    </div>
                )}

                {(item.submitted_by_name || item.contributor_name) && (
                    <div className="mt-3 text-xs text-neutral-500 flex items-center gap-1">
                        <ClipboardText size={12} />
                        {item.submitted_by_name || item.contributor_name}
                        {item.submitted_by_email && ` <${item.submitted_by_email}>`}
                    </div>
                )}

                {kind === "dataset" && <div className="mt-4 text-xs text-violet-700">One approval includes {[
                    ["item",ctx.material_type], ["process",ctx.process], ["parameter",ctx.parameter]
                ].filter(([,node])=>node?.status === "pending").map(([label,node])=>`${label}: ${node.name}`).join(" · ") || "this value; its directory path already exists"}. Other submitted values remain in review.</div>}
                <details className="mt-4 text-xs"><summary className="cursor-pointer text-violet-700">Full source and measurement details</summary><dl className="mt-3 grid grid-cols-2 gap-3">{["citation","doi","functional_unit","system_boundary","allocation_method","technology","data_quality","open_licence"].map(key=><div key={key}><dt className="text-neutral-500">{key.replaceAll("_"," ")}</dt><dd className="break-words">{typeof item[key] === "boolean" ? (item[key] ? "Yes" : "No") : item[key] || "—"}</dd></div>)}</dl></details>
                {item.notes && (
                    <div className="mt-3 border-l-2 border-neutral-200 pl-3 text-xs text-neutral-600 leading-relaxed">
                        {item.notes}
                    </div>
                )}
            </div>

            <div className="px-4 py-3 border-t border-neutral-100 bg-[#FCFCFB] flex items-center justify-end gap-2">
                <button
                    data-testid={TEST_IDS.adminRejectButton}
                    disabled={busy}
                    onClick={onReject}
                    className="px-3 py-1.5 text-xs border border-neutral-300 rounded-sm hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-40 flex items-center gap-1"
                >
                    <XCircle size={12} weight="fill" /> Reject
                </button>
                <button
                    data-testid={TEST_IDS.adminApproveButton}
                    disabled={busy}
                    onClick={onApprove}
                    className="px-3 py-1.5 text-xs bg-[#16A34A] text-white rounded-sm hover:bg-[#15803D] disabled:opacity-40 flex items-center gap-1"
                >
                    <CheckCircle size={12} weight="fill" />
                    {busy ? "Working…" : "Approve contribution"}
                </button>
            </div>
        </div>
    );
}

const Kpi = ({ k, v }) => (
    <div>
        <div className="label-caps text-[9px]">{k}</div>
        <div className="text-xs mt-0.5">{v || "—"}</div>
    </div>
);
